
import { useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import Navigation from '../../components/feature/Navigation';
import { useSEO } from '../../utils/seo';

// 3D模型组件
function Model({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  
  // 自动调整模型大小和位置
  if (scene) {
    scene.position.set(0, 0, 0);
    scene.rotation.set(0, 0, 0);
  }
  
  return <primitive object={scene} scale={1.5} />;
}

// 精美的3D占位组件 - 龙纹茶盒
function ModelPlaceholder() {
  return (
    <group>
      {/* 主体盒子 - 金丝楠木质感 */}
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.5, 3, 2]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          metalness={0.9} 
          roughness={0.1}
        />
      </mesh>
      
      {/* 顶部装饰 - 大漆工艺 */}
      <mesh position={[0, 1.8, 0]} castShadow>
        <boxGeometry args={[2.8, 0.3, 2.3]} />
        <meshStandardMaterial 
          color="#B8941E" 
          metalness={0.95} 
          roughness={0.05}
        />
      </mesh>
      
      {/* 底部装饰 */}
      <mesh position={[0, -1.8, 0]} castShadow>
        <boxGeometry args={[2.8, 0.3, 2.3]} />
        <meshStandardMaterial 
          color="#B8941E" 
          metalness={0.95} 
          roughness={0.05}
        />
      </mesh>
      
      {/* 龙纹装饰线条 - 正面 */}
      <mesh position={[0, 0, 1.05]}>
        <planeGeometry args={[2, 2.5]} />
        <meshStandardMaterial 
          color="#8B7355" 
          metalness={0.7} 
          roughness={0.3}
          opacity={0.8}
          transparent
        />
      </mesh>
      
      {/* 侧面装饰 - 左 */}
      <mesh position={[-1.3, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[1.8, 2.5]} />
        <meshStandardMaterial 
          color="#8B7355" 
          metalness={0.6} 
          roughness={0.4}
          opacity={0.7}
          transparent
        />
      </mesh>
      
      {/* 侧面装饰 - 右 */}
      <mesh position={[1.3, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[1.8, 2.5]} />
        <meshStandardMaterial 
          color="#8B7355" 
          metalness={0.6} 
          roughness={0.4}
          opacity={0.7}
          transparent
        />
      </mesh>
      
      {/* 中央装饰环 */}
      <mesh position={[0, 0, 1.1]}>
        <torusGeometry args={[0.4, 0.08, 16, 32]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          metalness={1} 
          roughness={0}
        />
      </mesh>
      
      {/* 顶部龙纹标识 */}
      <mesh position={[0, 1.5, 1.2]}>
        <circleGeometry args={[0.3, 32]} />
        <meshStandardMaterial 
          color="#FFD700" 
          metalness={1} 
          roughness={0}
          emissive="#D4AF37"
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}

export default function Series() {
  const [selectedDetail, setSelectedDetail] = useState<string | null>(null);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  
  // 3D模型路径
  const modelPath = 'https://dshelperimg.oss-cn-beijing.aliyuncs.com/other/Dragon_s_Lair_Box_1207164146_texture.glb';

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '国风·王朝系列 - 懂藏高端普洱茶典藏 | 传统工艺与时间艺术',
    description: '懂藏国风王朝系列,采用金丝楠木与天然大漆工艺,2015年春茶陈化9年。龙纹雕刻,双层气密设计,全球限量999套。适用于重要传承、商务会晤、庆典时刻。懂源懂韵,品鉴岁月沉淀的珍贵茶品。',
    keywords: '懂藏,国风王朝,高端普洱茶,金丝楠木茶盒,天然大漆,陈年普洱,限量茶叶,商务礼品,传统工艺',
    canonical: `${siteUrl}/series`,
    ogType: 'product',
    schema: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "国风·王朝",
      "description": "懂藏国风王朝系列,采用金丝楠木与天然大漆工艺,2015年春茶陈化9年",
      "brand": {
        "@type": "Brand",
        "name": "懂藏"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/LimitedAvailability",
        "itemCondition": "https://schema.org/NewCondition"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "168"
      }
    }
  });

  const seriesInfo = {
    name: '国风·王朝',
    code: 'DC-GF-001',
    value: '懂源懂韵',
    scenes: [
      { icon: 'ri-gift-line', text: '重要传承' },
      { icon: 'ri-team-line', text: '商务会晤' },
      { icon: 'ri-trophy-line', text: '庆典时刻' },
      { icon: 'ri-home-heart-line', text: '家族聚会' }
    ]
  };

  const productDetails = [
    { id: 'lacquer', name: '漆面工艺', desc: '七层天然大漆,历时180天' },
    { id: 'pattern', name: '龙纹雕刻', desc: '传统錾刻技法,纯手工完成' },
    { id: 'seal', name: '密封系统', desc: '双层气密设计,恒温恒湿' },
    { id: 'material', name: '木材选择', desc: '百年金丝楠木,自然风干' }
  ];

  const specs = [
    { label: '容量', value: '500g' },
    { label: '材质', value: '金丝楠木 / 天然大漆' },
    { label: '工艺', value: '传统手工 / 现代密封' },
    { label: '产地', value: '福建武夷山' },
    { label: '年份', value: '2015年春茶' },
    { label: '转化', value: '9年陈化' },
    { label: '适饮', value: '即饮 / 可再藏10年' },
    { label: '限量', value: '全球999套' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E4E2] overflow-x-hidden">
      <Navigation />
      
      <div className="flex flex-col lg:flex-row min-h-screen pt-16 lg:pt-16 overflow-x-hidden">
        {/* Left Info Panel */}
        <div className="w-full lg:w-[30%] border-b lg:border-b-0 lg:border-r border-[#D4AF37]/20 p-6 md:p-12 lg:p-16 flex flex-col justify-center space-y-8 lg:space-y-16">
          <div>
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-50 tracking-widest mb-2">
              SERIES
            </div>
            <h1 className="text-[16px] md:text-[18px] lg:text-[20px] text-[#D4AF37] font-[500] tracking-wide mb-2">
              {seriesInfo.name}
            </h1>
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-60 tracking-wider">
              {seriesInfo.code}
            </div>
          </div>

          <div>
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-50 tracking-widest mb-3">
              价值定位
            </div>
            <div className="text-[14px] md:text-[16px] text-[#D4AF37] tracking-[0.3em]">
              {seriesInfo.value}
            </div>
          </div>

          <div>
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-50 tracking-widest mb-4 md:mb-6">
              适用场景
            </div>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {seriesInfo.scenes.map((scene, index) => (
                <div key={index} className="flex flex-col items-center space-y-2 group cursor-pointer">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-[#D4AF37]/30 group-hover:border-[#D4AF37] transition-all duration-300">
                    <i className={`${scene.icon} text-[18px] md:text-[20px] text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity duration-300`} />
                  </div>
                  <span className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-70 tracking-wide text-center">
                    {scene.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Link
            to="/archive"
            className="inline-flex items-center space-x-2 text-[9px] md:text-[10px] text-[#D4AF37] tracking-wider group cursor-pointer whitespace-nowrap"
          >
            <span className="border-b border-transparent group-hover:border-[#D4AF37] transition-all duration-300">
              查看时间档案
            </span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>

        {/* Right Product Display */}
        <div className="w-full lg:w-[70%] flex flex-col overflow-x-hidden">
          {/* 3D Product View */}
          <div className="flex-1 flex items-center justify-center relative p-6 md:p-12 lg:p-16 overflow-hidden">
            <div className="w-full h-[400px] md:h-[500px] lg:h-[600px] touch-pan-y">
              <Canvas
                camera={{ position: [0, 0, 8], fov: 45 }}
                style={{ background: 'transparent', touchAction: 'none' }}
                shadows
              >
                <Suspense fallback={null}>
                  {/* 增强光照系统 */}
                  <ambientLight intensity={1.5} />
                  <directionalLight 
                    position={[5, 5, 5]} 
                    intensity={2} 
                    castShadow
                    shadow-mapSize-width={2048}
                    shadow-mapSize-height={2048}
                  />
                  <directionalLight position={[-5, 5, 5]} intensity={1.2} />
                  <directionalLight position={[0, 5, -5]} intensity={1} />
                  <pointLight position={[0, 5, 0]} intensity={1} />
                  <pointLight position={[0, -5, 0]} intensity={0.6} />
                  <spotLight 
                    position={[0, 8, 0]} 
                    intensity={0.8} 
                    angle={0.6} 
                    penumbra={1}
                    castShadow
                  />
                  
                  {/* 显示占位模型 */}
                  <ModelPlaceholder />
                  
                  <OrbitControls
                    enableZoom={true}
                    enablePan={false}
                    minDistance={4}
                    maxDistance={15}
                    autoRotate={true}
                    autoRotateSpeed={1.5}
                    enableDamping={true}
                    dampingFactor={0.05}
                    maxPolarAngle={Math.PI / 1.5}
                    minPolarAngle={Math.PI / 3}
                  />
                </Suspense>
              </Canvas>
            </div>

            {/* 3D展示标识 */}
            <div className="absolute top-6 md:top-8 left-1/2 -translate-x-1/2 pointer-events-none">
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg px-6 py-3 backdrop-blur-sm">
                <div className="text-[9px] text-[#D4AF37] tracking-wide text-center flex items-center space-x-2">
                  <i className="ri-3d-cube-line text-[12px]" />
                  <span>3D 产品展示</span>
                </div>
              </div>
            </div>

            {/* 操作提示 */}
            <div className="absolute bottom-6 md:bottom-8 lg:bottom-12 left-1/2 -translate-x-1/2 pointer-events-none">
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg px-6 py-3 backdrop-blur-sm">
                <div className="text-[8px] text-[#E5E4E2] opacity-60 tracking-wide text-center">
                  拖拽旋转 · 滚轮缩放
                </div>
              </div>
            </div>
          </div>

          {/* Product Details - Mobile */}
          <div className="lg:hidden border-t border-[#D4AF37]/20 p-6 space-y-4">
            {productDetails.map((detail) => (
              <div key={detail.id} className="flex items-start space-x-3 pb-4 border-b border-[#D4AF37]/10 last:border-b-0">
                <div className="w-8 h-8 flex items-center justify-center border border-[#D4AF37]/30 flex-shrink-0">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] text-[#D4AF37] mb-1">{detail.name}</div>
                  <div className="text-[9px] text-[#E5E4E2] opacity-70">{detail.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Product Specs */}
          <div className="border-t border-[#D4AF37]/20 px-6 md:px-12 lg:px-16 py-8 md:py-10 lg:py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 md:gap-x-8 lg:gap-x-12 gap-y-4 md:gap-y-6">
              {specs.map((spec, index) => (
                <div key={index} className="space-y-1">
                  <div className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-50 tracking-wider">
                    {spec.label}
                  </div>
                  <div className="text-[9px] md:text-[10px] text-[#D4AF37] tracking-wide">
                    {spec.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
